import type { FloorTodayOverview } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { NO_AMOUNT, timeText } from "../../i18n/intl";

/** S5: 24-hour, per the owner's ruling. `hour: "numeric"` gave "5:30 PM". */
function fmtTime(iso: string | null): string {
  if (!iso) return NO_AMOUNT;
  return timeText(new Date(iso));
}

interface ShopFloorShiftCardProps {
  overview: FloorTodayOverview | null;
  loading: boolean;
  error: string | null;
}

/** Passive sidebar status; shift actions live in the employee profile panel. */
export function ShopFloorShiftCard({
  overview,
  loading,
  error,
}: ShopFloorShiftCardProps) {
  const { t } = useT();
  if (loading && !overview) {
    return <p className="muted shop-floor-shift-summary">{t("shift.loading")}</p>;
  }

  if (!overview) {
    return (
      <p className="muted shop-floor-shift-summary">
        {error ? t("shift.unavailable") : t("shift.noneToday")}
      </p>
    );
  }

  if (overview.activeShift) {
    return (
      <p className="muted shop-floor-shift-summary">
        {t("shift.fromTill", {
          from: fmtTime(overview.activeShift.actualStart),
          till: fmtTime(overview.activeShift.plannedEnd),
        })}
      </p>
    );
  }

  if (overview.nextScheduledShift) {
    return (
      <p className="muted shop-floor-shift-summary">
        {t("shift.today", {
          from: fmtTime(overview.nextScheduledShift.plannedStart),
          till: fmtTime(overview.nextScheduledShift.plannedEnd),
        })}
      </p>
    );
  }

  if (overview.latestCompletedShift) {
    return (
      <p className="muted shop-floor-shift-summary">
        {t("shift.endedAt", { time: fmtTime(overview.latestCompletedShift.actualEnd) })}
      </p>
    );
  }

  return (
    <p className="muted shop-floor-shift-summary">
      {t("shift.noneToday")}
    </p>
  );
}
